import { api } from './client'
import { requireArrayIn } from './readShape'
import { savedRunErrorSentence } from '@components/reports/queue/resultRetention'

/**
 * The two read endpoints DISAGREE ON CASING, and neither is a superset:
 *
 *   GET /reports/jobs      → raw D1 snake_case. Has `id`, `report_id`, `action_key`,
 *                            `report_type`, `row_count`, `duration_ms`, `expires_at`.
 *                            Has NO `error` (not in the list projection).
 *   GET /reports/jobs/:id  → camelCase. Has `jobId` (not `id`), `error`, `savedParams`.
 *                            Has NO `expires_at` and NO `action_key`.
 *
 * (Verified against live QA payloads on 2026-08-06, not inferred from the route code.)
 *
 * Spec 132 made the detail a superset for everything the drawer reads — it now also
 * carries `id`, `scheduleId` (only when the row has the column), `actionKey`,
 * `reportType` and `expiresAt`. An OLDER worker still sends the shape above, which is
 * why `scheduleKnown` exists and why the merge rules below are unchanged.
 *
 * So merging a status response over a list row must NOT clobber `expiresAt`/`actionKey`
 * with undefined — `mergeJob` below only overwrites keys the source actually carries.
 * Normalize ONCE here; every consumer reads the normalized shape and no new code
 * spreads `??` fallbacks around.
 */
export function normalizeJob(raw) {
  if (!raw || typeof raw !== 'object') return null
  const pick = (...keys) => {
    for (const k of keys) if (raw[k] !== undefined && raw[k] !== null) return raw[k]
    return null
  }
  // Did the payload carry the key at all (even as null)? `pick` cannot say.
  const hasKey = (...keys) => keys.some((k) => k in raw)
  const num = (v) => (v === null || v === undefined || v === '' ? null : Number(v))

  const createdAt = num(pick('created_at', 'createdAt'))
  const durationMs = num(pick('duration_ms', 'durationMs'))

  return {
    id: pick('id', 'jobId'),
    kind: pick('kind'),
    // reportId stays a STRING key for route building (D1 stores it as text).
    reportId: pick('report_id', 'reportId') != null ? String(pick('report_id', 'reportId')) : null,
    actionKey: pick('action_key', 'actionKey'),
    reportType: pick('report_type', 'reportType'),
    // The CB processId — REQUIRED by the generic-report reopen path
    // (ReportView ?jobId → reportService.getData(reportId, processId)). Dropping it
    // made every generic reopen 400 with "processId required".
    processId: pick('process_id', 'processId'),
    state: pick('state') || 'queued',
    percent: num(pick('percent')) ?? 0,
    message: pick('message'),
    rowCount: num(pick('row_count', 'rowCount')),
    durationMs,
    createdAt,
    updatedAt: num(pick('updated_at', 'updatedAt')),
    expiresAt: num(pick('expires_at', 'expiresAt')),
    /*
     * Spec 152 — when the stored RESULT stops being readable: the earlier of the row's
     * `expires_at` and the results bucket's age (cb-api `resultRetention.js`). Read by
     * `queueConfig.resultExpiry`, so a schedule's run — `expires_at` null — finally
     * expires when its object does. `resultMaxAgeDays` is that age, for the retention
     * sentences. Both null on an older worker (and where the age is undeclared): the
     * client then reads `expiresAt` alone, exactly as before.
     */
    resultExpiresAt: num(pick('resultExpiresAt', 'result_expires_at')),
    resultMaxAgeDays: num(pick('resultMaxAgeDays')),
    // When a "queue for later" run is due to fire (epoch ms). Set only while the
    // job is `scheduled`; the queue shows this as "Runs …", not a finish time.
    scheduledFor: num(pick('scheduled_for', 'scheduledFor')),
    /*
     * The schedule that produced this run, or null for an ad-hoc one.
     *
     * Small field, disproportionate consequences — it is the ONLY thing that
     * distinguishes the two populations of run in the queue, and they behave
     * differently in ways the UI states out loud:
     *
     *   - RETENTION. An ad-hoc result lives on a 1-week TTL; a scheduled one has
     *     `expires_at = null` and is kept BY COUNT (`retain_count`, default 5),
     *     pruned when the schedule fires and again as each run finishes, AND its
     *     stored data is deleted by the
     *     results bucket's age rule (spec 152: `resultExpiresAt`, from the worker's
     *     RESULT_MAX_AGE_DAYS), whichever comes first. Any sentence about how long a
     *     result survives is false for one population unless it can branch, and
     *     without this field it cannot. Three branches in shipped code were
     *     already written against a `scheduleId` that never arrived, so they
     *     were dead code that read as working logic.
     *   - PROVENANCE. "from Monday A/R pack" vs "queued manually", and the
     *     recurring glyph on a queue row (Himaad's Section 2, rows 4 and 5).
     *
     * It is on the wire already (`schedule_id` on the jobs row, set by
     * `runner.js` when a schedule fires) — the client simply never picked it up.
     */
    scheduleId: pick('schedule_id', 'scheduleId'),
    /*
     * Spec 132 — did the payload SAY whether a schedule produced this run?
     *
     * `pick` collapses an absent key and a null value into the same `null`, and for
     * `scheduleId` those are two different facts: a list row always carries
     * `schedule_id` (null = ad-hoc), while the detail endpoint before spec 132 never
     * carried it at all. Read through `pick`, every detail-only run — every scheduled
     * run reached from the Schedules page — was confidently "Queued manually".
     *
     * `true` when the key is present, `null` (NEVER `false`) when it is not: `mergeJob`
     * skips nulls, so an older worker's detail merged over a list row cannot clobber
     * the row's `true`. `jobTrigger` reads a null here as "Not recorded".
     */
    scheduleKnown: ('schedule_id' in raw || 'scheduleId' in raw) ? true : null,
    error: pick('error'),
    /*
     * Whether this result's EMAIL was refused, and why (QA, 2026-09-03).
     *
     * A run can be perfectly `ready` and its email still have failed — a
     * completely separate outcome that had no representation anywhere. The cron
     * knew (CB hands back its own accept/reject text) and threw it away, so the
     * owner saw a green "Ready" row, no email, and nothing to act on. Measured on
     * QA: five jobs, one owner, zero delivered, zero visible reason.
     *
     * Spec 137 replaced the "deliveredAt means delivered" reading this note used to
     * describe: what the drawer says is decided by `deliveryStatus` (below), in
     * `deliveryOutcome.js`, and it never says "delivered".
     *
     * *review* — `deliverEmail` and `deliveryAttempts` are NULL when the payload does
     * not carry the key, never `false`/`0`. `mergeJob` skips nulls, so a status payload
     * without delivery keys (a split deploy, a D1 missing a column — the detail omits
     * the key then, spec 137 deviation 2) can no longer clobber a list row's `true` and
     * hide the whole Email row, or reset its count of tries. The defaults are applied
     * where they are read (`deliveryOutcome`). Same absent-versus-null rule as
     * `scheduleKnown` above.
     */
    deliveryError: pick('delivery_error', 'deliveryError'),
    deliveryAttempts: num(pick('delivery_attempts', 'deliveryAttempts')),
    deliverEmail: hasKey('deliver_email', 'deliverEmail')
      ? (pick('deliver_email', 'deliverEmail') === 1 || pick('deliver_email', 'deliverEmail') === true)
      : null,
    /*
     * Spec 137 — the API now SENDS the delivery fields (owner rows only; a colleague's
     * row carries none of them), as `deliveredAt` / `deliveryStatus` / `deliveryNote` /
     * `deliverFormat`. Both casings are still read, because a D1-shaped row (a test
     * capture, an older worker) carries the column names.
     *
     * `deliveredAt` is `notified_at`, the send-once guard, and it means "delivery
     * FINISHED, whatever the outcome" — NOT "sent". The live give-up row (D1 fbe36df5:
     * `notified_at` + `delivery_error`, 10 attempts) has it set. `deliveryStatus` is the
     * outcome (accepted / unconfirmed / failed / skipped); null means pending, or a row
     * written before migration 0015. Read the status, never `deliveredAt`, to say what
     * happened — `deliveryOutcome.js` is the one place that does.
     */
    deliveredAt: num(pick('deliveredAt', 'notified_at', 'notifiedAt')),
    deliveryStatus: pick('delivery_status', 'deliveryStatus'),
    deliveryNote: pick('delivery_note', 'deliveryNote'),
    deliverFormat: pick('deliver_format', 'deliverFormat'),
    /*
     * Spec 138 — the run's OTHER recipients and each one's outcome, `[{to, status,
     * error, note, attempts}]`, sent on the owner's rows only (a colleague's row carries
     * no key at all, and `cbEmailId` never leaves cb-api). Same absent-versus-null rule
     * as above: absent ⇒ null, so a merge cannot clobber a list row's array. cb-api
     * answers `null` for a stored list it cannot read; that is said separately
     * (`deliveryRecipientsUnreadable`), never folded into "no other recipients".
     */
    deliveryRecipients: normalizeRecipients(raw),
    deliveryRecipientsUnreadable: hasKey('deliveryRecipients') && !Array.isArray(raw.deliveryRecipients) ? true : null,
    savedParams: pick('savedParams', 'saved_params'),
    // CC-50150: firm/group scope can list OTHER people's runs. Only an owned run
    // is openable — its stored result carries its owner's row-level data scope,
    // so serving it to a colleague would leak rows CB never showed them. Absent
    // (own-scope listing, or the status endpoint) means "mine", hence !== false.
    isOwner: pick('isOwner', 'is_owner') !== false,
    /*
     * Spec 169 (CC-54214) — who the run is LISTED to, and whether THIS reader may open it
     * although it is not theirs. All three are cb-api's answer, never derived here:
     *   privateListing   listed only to its owner and Centerbase administrators (a 'mine'
     *                    schedule's run, or a deleted schedule's — cb-api fails closed);
     *   privateSchedule  its schedule is provably 'mine';
     *   adminAccess      the reader is a CB administrator (CB said so on the request) and
     *                    the run is another user's private-schedule run: Open / Download.
     * Each is true only for a literal `true`; absent (an older worker) is null, never
     * false, so a merge cannot clobber a list row's answer (same rule as `scheduleKnown`).
     */
    privateListing: hasKey('privateListing') ? raw.privateListing === true : null,
    privateSchedule: hasKey('privateSchedule') ? raw.privateSchedule === true : null,
    adminAccess: hasKey('adminAccess') ? raw.adminAccess === true : null,
    // There is NO `completed_at` column. Derive the finish time; `updatedAt` is the
    // better source once a job is terminal (it's the moment state last changed).
    finishedAt: createdAt != null && durationMs != null ? createdAt + durationMs : num(pick('updated_at', 'updatedAt')),
  }
}

/** Spec 138: `deliveryRecipients` as the API sends it, normalised; null when the payload had none. */
function normalizeRecipients(raw) {
  const list = raw?.deliveryRecipients
  if (!Array.isArray(list)) return null
  return list
    .filter((r) => r && typeof r === 'object' && typeof r.to === 'string' && r.to)
    .map((r) => ({
      to: r.to,
      status: typeof r.status === 'string' ? r.status : null,
      error: r.error ?? null,
      note: r.note ?? null,
      attempts: r.attempts == null || r.attempts === '' ? 0 : Number(r.attempts) || 0,
    }))
}

/**
 * Merge a fresh status onto a known list row WITHOUT dropping list-only fields.
 * A plain `{...listRow, ...status}` would work today only by luck (the status
 * object simply omits those keys); normalizing both first makes the omitted keys
 * explicit `null`, so we must merge key-by-key instead.
 */
export function mergeJob(base, incoming) {
  if (!base) return incoming
  if (!incoming) return base
  const out = { ...base }
  for (const [k, v] of Object.entries(incoming)) {
    if (LIST_AUTHORITATIVE.has(k)) continue
    if (v !== null && v !== undefined) out[k] = v
  }
  // State must always win from the fresher source, even when falsy-ish.
  if (incoming.state) out.state = incoming.state
  return out
}

/**
 * Keys whose value comes from the LIST payload and must survive a status refresh.
 *
 * `isOwner` is the security-relevant one (CC-50231). It is only meaningful in a
 * firm/group listing, where the server computes it per row. The status endpoint is
 * owner-scoped, so its answer is unconditionally "mine" — merging that over a list
 * row would flip a foreign row to owned and restore every affordance this ticket
 * removes. Two accidents masked it (only active-state rows are polled, and the 404
 * is swallowed), which is exactly the kind of unobservable fail-soft this repo keeps
 * getting caught by, so it is closed here rather than left to luck.
 *
 * The server now also emits `isOwner: true` from the status route, so this guard is
 * the second of two independent defences: this one holds even against an older worker
 * that omits the field, where `normalizeJob`'s absent-value default is fail-OPEN.
 *
 * Spec 169: `adminAccess` joins it. The list row's value was computed by the server for
 * this reader at this scope; a detail read never widens what a listed row offers.
 */
const LIST_AUTHORITATIVE = new Set(['isOwner', 'adminAccess'])

/**
 * The delivery outcome, as ONE group (spec 137, *review*).
 *
 * The Queue page merges a run's detail over its POLLED list row to open the drawer
 * (`ReportQueue.jsx`). The detail is read once when the drawer opens (30s stale time,
 * no refetch on focus); the list row is refreshed every poll. Merged key by key, a
 * snapshot taken during retry 3 kept its attempts and its error over a list row that
 * had since given up at 10 — "Couldn't be sent after 3 tries" with a stale reason. So
 * when the list row carries the delivery projection at all, the whole group comes from
 * it, nulls included (a later accept CLEARS the error); fields from two snapshots are
 * never mixed.
 */
export const DELIVERY_FIELDS = Object.freeze([
  'deliverEmail', 'deliverFormat', 'deliveryStatus', 'deliveredAt', 'deliveryError', 'deliveryAttempts', 'deliveryNote',
  // Spec 138: the per-recipient outcomes belong to the same group, for the same reason.
  'deliveryRecipients', 'deliveryRecipientsUnreadable',
])

/** `merged` with the delivery group taken whole from `listRow`, when the list row has it. */
export function preferListDelivery(merged, listRow) {
  if (!merged || !listRow || listRow.deliverEmail == null) return merged
  const out = { ...merged }
  for (const k of DELIVERY_FIELDS) out[k] = listRow[k] ?? null
  return out
}

/**
 * Spec 152 review — a saved run that retention removed fails in PLAIN WORDS.
 *
 * Every reopen shows the error's `message`: the report page's `?jobId=` reopen and all 21
 * specialized pages' (`useLatestRun` → each page's `runErrorMessage` → `err.message`).
 * For a run whose stored result the bucket aged out (410) or whose row retention deleted
 * (404 "Job not found" — a schedule's "last N" now deletes the moment N newer runs
 * finish, so the "Open the report" link in a scheduled email can outlive its run) that
 * message was axios's "Request failed with status code 410 / 404".
 *
 * The SAME error object is rethrown — `response` intact, so `isResultGone(err)` and every
 * status check still work — with only its message replaced. Any other failure is
 * rethrown untouched.
 */
const inPlainWords = (err) => {
  const sentence = savedRunErrorSentence(err)
  if (sentence && err && typeof err === 'object') err.message = sentence
  throw err
}

/** Spec 176 — status reads in flight, by job id: `{ promise, at }` (see `status`). */
const inflightStatus = new Map()
/** A shared read older than this is not shared again (axios's own timeout is 30s). */
const STATUS_SHARE_MAX_MS = 35_000

/**
 * Report Center async jobs (spec 055). Create a job, poll its status, fetch the
 * result from R2 when ready. Mirrors cb-api /reports/jobs. Server decides
 * shareability/security — the client never asserts it.
 */
const reportJobService = {
  /**
   * POST /reports/jobs → { jobId, state, reused, resultKey?, securityClass? }
   *
   * `config` (optional) is axios's request config, passed through as given — e.g.
   * `{ timeout }` for spec 209's Advanced Report door, which can read the user's Exago list
   * (≤ 45 s, after a ≤ 5 s probe) before it answers, past the client's 30 s default. Sent as
   * `api.post`'s third argument ONLY when given, so every other caller's request is exactly
   * what it was.
   */
  create: async ({ kind = 'generic', reportId, accountingKey, actionKey, endpoint, filters = [], hideRates, scheduledFor, deliverEmail, deliverFormat } = {}, config) => {
    // `endpoint` is the replay spec { method, path, body } for specialized/accounting
    // jobs — it MUST be forwarded or the runner has no path to replay ("malformed").
    // `scheduledFor` (epoch ms, "Queue for later") MUST be forwarded too: without it
    // the backend has no run time, so the job is created `queued` and advanced
    // immediately instead of parking as `scheduled` until the cron fires it.
    // deliver* (spec 059) opt into emailing the ready result. Self-only, and now
    // enforced rather than merely defaulted: CC-53046 removed `deliverTo` from the
    // API entirely — the server derives the recipient from the caller's verified
    // token, and REJECTS a body that names anyone else. No call site ever set it
    // (every caller passes only deliverEmail + deliverFormat), so this parameter was
    // pure attack surface. Do not re-add it; a genuine send-to-a-colleague feature
    // needs the recipient AUTHORISED against CB's directory, not just accepted.
    // ⚠ Superseded for SCHEDULES by spec 138 (2026-10-07): a schedule's
    // `deliverRecipients` (reportScheduleService) emails up to 25 validated addresses (spec 201).
    // An ad-hoc job stays self-only, and cb-api refuses `deliverRecipients` here (400).
    const body = {
      kind, reportId, accountingKey, actionKey, endpoint, filters, hideRates, scheduledFor,
      deliverEmail, deliverFormat,
    }
    const data = config === undefined
      ? await api.post('/reports/jobs', body)
      : await api.post('/reports/jobs', body, config)
    return data?.data
  },

  /**
   * GET /reports/jobs/:id → normalized job (each call advances the job server-side).
   *
   * Spec 176 (CC-54206): ONE read per job in flight per tab. Three things read a running
   * job's status on the report page — the page's own 1.5s loop, the Queue badge and the
   * bell (each a `useMyReports` every 5s) — and each read advances the job on the server.
   * Measured on QA 2026-10-13, report 121: those reads were held 17–24s each, so they
   * stacked into a pile of concurrent advances of the same job, and the page's own read
   * then outlived axios's 30s timeout. A caller that asks while a read of the same job is
   * already out now shares that read's answer instead of starting another.
   */
  status: (jobId) => {
    const key = String(jobId)
    const pending = inflightStatus.get(key)
    // The age bound is belt-and-braces: axios settles every read within its 30s timeout,
    // but a read the client interceptor parks forever (the 401 → re-login path) must not
    // answer for this job for the rest of the tab's life.
    if (pending && Date.now() - pending.at < STATUS_SHARE_MAX_MS) return pending.promise
    const promise = (async () => {
      const data = await api.get(`/reports/jobs/${jobId}`).catch(inPlainWords)
      return normalizeJob(data?.data) || { id: jobId, state: 'failed', percent: 0 }
    })().finally(() => { if (inflightStatus.get(key)?.promise === promise) inflightStatus.delete(key) })
    inflightStatus.set(key, { promise, at: Date.now() })
    return promise
  },

  /** GET /reports/jobs/:id/result → the report payload (gzip is decoded by the browser). */
  result: async (jobId) => api.get(`/reports/jobs/${jobId}/result`).catch(inPlainWords),

  /**
   * GET /reports/jobs → jobs, normalized, newest first.
   *
   * `scope` ('mine' | 'group' | 'firm'), `state`, `limit` and `cursor` are all
   * server-side (CC-50150). The audience for 'group' is resolved from CB by the
   * WORKER — never sent from here — and the server falls back to 'mine' with
   * `scopeResolved:false` if that lookup fails, so the caller can say "we couldn't
   * confirm your group" instead of showing a silently-narrowed list.
   *
   * Returns the envelope, not a bare array, because the pager and the tab counts
   * need `nextCursor` / `total`. `listAll` keeps the old array-only shape for
   * callers that just want the tray.
   */
  list: async ({ scope, state, limit, cursor } = {}) => {
    const data = await api.get('/reports/jobs', { scope, state, limit, cursor })
    // CC-53047 — an unreadable response and an empty queue must not look the same.
    // This was `data?.data || {}` then `(d.jobs || []).map(...)`: two fallbacks that
    // turned a 200 carrying the wrong shape into a cheerful empty queue, with nothing
    // anywhere saying a read had failed. The sixth instance of this family; the guard
    // now lives in one place (see readShape.js) rather than being copied a third time.
    //
    // Note this closes only the WRONG-SHAPE half. A 503/504/403/abort rejects at the
    // axios layer and needs a caller that stops swallowing it — that is the other half,
    // in useMyReports. Both are required, which is why this bug had five failure modes.
    const rawJobs = requireArrayIn(data, 'jobs', { noun: 'queue', entryNoun: 'run' })
    const d = data?.data || {}
    return {
      // `.filter(Boolean)` stays, but can no longer hide a malformed body: normalizeJob
      // returns null for a non-object, and a whole array of those would previously have
      // yielded `[]` with no signal. requireArrayIn has already rejected that shape.
      jobs: rawJobs.map(normalizeJob).filter(Boolean),
      nextCursor: d.nextCursor ?? null,
      total: d.total ?? null,
      // CC-53049: the per-state breakdown across the WHOLE scope, so the queue's tab
      // counts describe every run rather than whatever page was fetched. `null` on an
      // older worker — the caller then counts the page and says so.
      //
      // ⚠️ This line was MISSING in the first cut, and nothing unit-level caught it:
      // `useMyReports.test.js` mocks this service, so it asserted a contract the service
      // did not actually fulfil. The server computed the counts and the client threw
      // them away — precisely the `inAnyGroup` mistake this same change set out to fix,
      // one layer further down. Found only by looking at the deployed page, where the
      // subtitle read "counts cover this page only" and "Didn't finish" showed 3 against
      // a server truth of 33. Mock the transport, not the module you are verifying.
      stateCounts: d.stateCounts ?? null,
      // Spec 152 review — the instant cb-api classified `stateCounts` against: a `ready`
      // run whose result had expired by then is counted under `expired`. Null on an older
      // worker, whose `ready` still includes them (the caller then subtracts what it sees).
      countedAt: Number.isFinite(d.countedAt) ? d.countedAt : null,
      scope: d.scope || 'mine',
      scopeResolved: d.scopeResolved !== false,
      // CC-50231 TC5b: whether the caller belongs to any CB group at all. `null` for
      // the mine/firm scopes — the question wasn't asked, which is NOT the same as
      // "asked, answer no". Lets the UI distinguish "you're in no group" (a real
      // answer) from "your group has no activity".
      inAnyGroup: d.inAnyGroup ?? null,
      // Spec 152 — the results bucket's age limit in days; null when the worker does
      // not declare one (an older worker, or an environment without the var).
      resultMaxAgeDays: d.resultMaxAgeDays ?? null,
      // Spec 169 — this answer includes other users' private-schedule runs because CB
      // confirmed, on this request, that the reader is an administrator. Literal true only.
      adminView: d.adminView === true,
    }
  },

  /**
   * DELETE /reports/jobs/:id — DISMISS. Removes the row from the tray.
   *
   * Not a cancel, despite what several call-site comments used to say: a live job
   * keeps running server-side after this, and only the row goes away. `cancel`
   * below is the one that stops the work.
   */
  remove: async (jobId) => api.delete(`/reports/jobs/${jobId}`),

  /**
   * POST /reports/jobs/:id/cancel — REAL cancel (cb-api `jobs/routes.js:453`).
   *
   * Marks the job `cancelled` so the runner's top-of-loop guard abandons it, and
   * deletes its per-user R2 result.
   *
   * ⚠️ What it does NOT do, and what any button wired to it may therefore not
   * promise: CB has no cancel hook, so the legacy query the job kicked off is not
   * abandoned and **may run to completion** on the database. The route's own
   * comment says so. Copy must stop at "stops waiting and discards the result".
   *
   * This route has existed since 2026-08-07. Five client comments claiming there
   * was no honest cancel outlived it, which is why the UI shipped a "Dismiss"
   * that quietly did less than the backend could — see spec 111.
   */
  cancel: async (jobId) => api.post(`/reports/jobs/${jobId}/cancel`, {}),
}

export default reportJobService
